/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Grid, Input, Popconfirm, Row, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import DocumentTitle from '../DocumentTitle';
import { LiveChatIcon } from '../Icons/LiveChatIcon';

// Misc imports
import { useFetchUserInvitationsQuery, useRevokeUserInvitationMutation } from '../../store/users/usersApi';
import { ADDITIONAL_ROLES_ORG, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE, PERMISSIONS_ORG, USER_INVITATION_STATUSES } from '../../constants/main';
import { IS_LIVECHAT_CONSOLE_ONLY, IS_LIVECHAT_CONVERSATIONS_ENABLED } from '../../constants/env';
import is from '../../utils/is';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

// Constant declarations
const { useBreakpoint } = Grid;
const { Text } = Typography;

const UserInvitationsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const screen = useBreakpoint();
  const [searchParams] = useSearchParams();

  const initialSearchParams = useCallback(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const filter = urlSearchParams.get('filter');
    return filter || null;
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilter, setSearchFilter] = useState({ search: '' });
  const [filterStatus, setFilterStatus] = useState(initialSearchParams());
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const { data: userInvitations, isFetching: isLoadingUserInvitations } = useFetchUserInvitationsQuery({ orgId, urlParams: {
    page: currentPage - 1,
    pageSize: pageSize,
    ...(searchFilter.search !== '' && { search: encodeURIComponent(searchFilter.search) }),
    ...(filterStatus && { filter: filterStatus })
  } });

  const [revokeUserInvitation] = useRevokeUserInvitationMutation();

  useEffect(() => {
    updateUrlSearchParams(Number(searchParams.get('page')) + 1);
  }, []);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const urlStatus = urlSearchParams.get('filter');

    if (!filterStatus && urlStatus) {
      urlSearchParams.delete('filter');
      // Set the filters to the url
      navigate({
        pathname: location.pathname,
        search: urlSearchParams.toString()
      }, { replace: true });
    }
  }, [filterStatus]);

  const updateUrlSearchParams = (current = 1, pageSize = DEFAULT_PAGINATION_SIZE, status = null) => {
    setCurrentPage(current);
    setPageSize(pageSize);
    const urlSearchParams = new URLSearchParams(location.search);

    urlSearchParams.set('page', current - 1);
    urlSearchParams.set('pageSize', pageSize);

    let filter = status || filterStatus;
    if (filter) {
      urlSearchParams.set('filter', filter);
    }

    // Set the filters to the url
    navigate({
      pathname: location.pathname,
      search: urlSearchParams.toString()
    }, { replace: true });
  };

  const handleTableChange = (pagination, filters) => {
    const status = filters?.status?.length ? filters.status[0] : null;
    setFilterStatus(status);

    updateUrlSearchParams(pagination.current, pagination.pageSize, status);
  };

  return (
    <Fragment>
      <DocumentTitle title={t('User Invitations')} />

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Input
            size="large"
            style={{ maxWidth: screen.xs ? '100%' : 280 }}
            placeholder={t('Search in contents')}
            allowClear
            prefix={<SearchOutlined className="text-muted" />}
            onChange={(e) => {
              setCurrentPage(1);
              setSearchFilter({ ...searchFilter, search: e.target.value });
            }}
          />
        </Col>

        <Col span={24}>
          <Table
            rowKey="id"
            size="small"
            dataSource={userInvitations && userInvitations.items}
            loading={isLoadingUserInvitations}
            onChange={handleTableChange}
            pagination={{
              size: 'default',
              total: userInvitations?.total,
              defaultCurrent: 1,
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${t('Total Invitations')}: ${total}`
            }}
            columns={[
              {
                key: 'email',
                dataIndex: 'email',
                title: t('Email'),
                ellipsis: true,
                render: (_text, record) => (
                  <Highlighter
                    highlightClassName="bg-highlight p-0"
                    className="strong"
                    autoEscape
                    searchWords={[searchFilter.search]}
                    textToHighlight={record.email}
                  />
                )
              },
              {
                title: t('Org Access'),
                key: 'orgAccess',
                responsive: ['xl'],
                render: (_text, record) => {
                  let permissions = [];

                  if (!is.emptyObject(record.organizationRoles) && record.organizationRoles[orgId]) {
                    const organizationRoles = record.organizationRoles[orgId].filter(role => Object.values(PERMISSIONS_ORG).includes(role));
                    if (organizationRoles.length) {
                      permissions.push(
                        <Tag key={organizationRoles[0]}>
                          {t(organizationRoles[0], { context: 'user role' })}
                        </Tag>
                      );
                    }

                    if ((IS_LIVECHAT_CONSOLE_ONLY || IS_LIVECHAT_CONVERSATIONS_ENABLED) && record.organizationRoles[orgId].find(role => role === ADDITIONAL_ROLES_ORG.LIVE_AGENT)) {
                      permissions.push(
                        <Tag key={ADDITIONAL_ROLES_ORG.LIVE_AGENT}>
                          <LiveChatIcon />
                        </Tag>
                      );
                    }
                  }

                  return (
                    permissions.length ? <Space size={1}>{permissions}</Space> : <Text type="secondary">-</Text>
                  );
                }
              },
              {
                title: t('Agent Access'),
                key: 'botAccess',
                responsive: ['xl'],
                render: (_text, record) => {
                  const tenantRolesByOrganization = Object.keys(record.tenantRoles[orgId] ?? {});

                  return (
                    is.array(tenantRolesByOrganization) && tenantRolesByOrganization.length > 0 ? (
                      <Tag>
                        {tenantRolesByOrganization.length} {tenantRolesByOrganization.length === 1 ? t('agent') : t('agents')}
                      </Tag>
                    ) : (
                      <Text type="secondary">-</Text>
                    )
                  );
                }
              },
              {
                title: t('Status'),
                dataIndex: 'status',
                key: 'status',
                filters:
                  Object.keys(USER_INVITATION_STATUSES).map(status => ({
                    text: status,
                    value: status
                  })),
                defaultFilteredValue: filterStatus ? [filterStatus] : null,
                filterMultiple: false,
                filterIcon: useTableFilterIcon,
                ellipsis: true,
                render: (_text, record) => {
                  switch (record.status) {
                    case USER_INVITATION_STATUSES.ACCEPTED:
                      return (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                          {record.status}
                        </Tag>
                      );
                    case USER_INVITATION_STATUSES.PENDING:
                      return (
                        <Tag icon={<ExclamationCircleOutlined />} color="warning">
                          {record.status}
                        </Tag>
                      );
                    case USER_INVITATION_STATUSES.EXPIRED:
                      return (
                        <Tag icon={<ClockCircleOutlined />} color="default">
                          {record.status}
                        </Tag>
                      );
                    case USER_INVITATION_STATUSES.REVOKED:
                      return (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                          {record.status}
                        </Tag>
                      );
                    default:
                      return;
                  }
                }
              },
              {
                key: 'expirationDate',
                dataIndex: 'expirationDate',
                title: t('Expiration Date'),
                ellipsis: true,
                render: (_text, record) => {
                  return moment(new Date(record.expirationDate)).format(DEFAULT_DATETIME_FORMAT);
                }
              },
              {
                key: 'createdBy',
                dataIndex: 'createdBy',
                title: t('Created By'),
                ellipsis: true,
                render: (_text, record) => {
                  return record.createdBy.name
                    ? <Text>{record.createdBy.name}</Text>
                    : <span className="text-secondary">
                      {t('(user unknown)')}
                    </span>;
                }
              },
              {
                key: 'actions',
                title: t('Actions'),
                align: 'center',
                width: 120,
                render: (_, record) => (
                  <Space size={2} wrap={false} align="center">
                    <Tooltip
                      overlayClassName="pointer-events-none"
                      className="ml-1"
                      placement="top"
                      title={t('Revoke invitation.')}
                    >

                      <Popconfirm
                        title={t('Are you sure you want to revoke this invitation?')}
                        placement="topRight"
                        okText={t('Revoke')}
                        onConfirm={async() => {
                          revokeUserInvitation({ orgId, userId: record.id });
                        }}
                      >
                        <Button
                          type="text"
                          shape="circle"
                          disabled={record.status !== USER_INVITATION_STATUSES.PENDING}
                          icon={<UndoOutlined />}
                        />
                      </Popconfirm>
                    </Tooltip>
                  </Space>
                )
              }
            ]}
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export default UserInvitationsTable;
